import { format, formatDistanceToNow, differenceInCalendarDays, parseISO, isValid } from 'date-fns';
import type { StrategyType, OptionAction, AlertType, AlertSeverity } from '@steward/shared';

/**
 * Convert a date (which may be a UTC timestamp from the DB) to a local-date-only Date object.
 * This prevents timezone shift when displaying dates like expirations that are stored as midnight UTC.
 * For Unix timestamps, extracts the UTC date parts. For Date objects and strings, returns as-is.
 */
export function toLocalDate(input: Date | string | number): Date {
  const d = typeof input === 'string' ? parseISO(input) : new Date(typeof input === 'number' && input < 1e12 ? input * 1000 : input);
  // Use UTC components to avoid timezone shift
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

// Currency formatting
/**
 * `cents: false` prints whole dollars (`$4,000`) for figures where the cents are noise —
 * capital held, what frees up on an expiration. It defaults to true, so every existing
 * caller's output is unchanged.
 */
export function formatCurrency(
  value: number | null | undefined,
  options?: { showSign?: boolean; cents?: boolean },
): string {
  const digits = options?.cents === false ? 0 : 2;
  if (value == null) return digits === 0 ? '$0' : '$0.00';

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

  const formatted = formatter.format(Math.abs(value));
  if (options?.showSign && value !== 0) {
    return value > 0 ? `+${formatted}` : `-${formatted}`;
  }
  return value < 0 ? `-${formatted}` : formatted;
}

export function formatPnl(value: number | null | undefined): string {
  return formatCurrency(value ?? 0, { showSign: true });
}

/**
 * Format a total credit/debit with per-share annotation.
 * Example: formatCreditWithPerShare(1500, 5) → "$1,500.00 (3.00)"
 * Per-share = total / contracts / 100.
 */
export function formatCreditWithPerShare(
  total: number,
  contracts: number,
  opts: { showSign?: boolean } = {},
): string {
  const totalStr = formatCurrency(total, opts);
  if (!contracts || contracts === 0) return totalStr;
  const perShare = total / contracts / 100;
  return `${totalStr} (${perShare.toFixed(2)})`;
}

export function formatPercent(value: number, decimals = 1): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(decimals)}%`;
}

export function formatCompactNumber(value: number): string {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

// Date formatting
export function formatDate(date: Date | string | null | undefined): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? parseISO(date) : date;
  if (!isValid(d)) return '-';
  return format(d, 'MMM d, yyyy');
}

export function formatDateTime(date: Date | string | null | undefined): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? parseISO(date) : date;
  if (!isValid(d)) return '-';
  return format(d, 'MMM d, yyyy h:mm a');
}

export function formatShortDate(date: Date | string | null | undefined): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? parseISO(date) : date;
  if (!isValid(d)) return '-';
  return format(d, 'MM/dd/yy');
}

export function formatExpirationDate(date: Date | string | null | undefined): string {
  if (!date) return '-';
  const parsed = typeof date === 'string' ? parseISO(date) : date;
  if (!isValid(parsed)) return '-';
  // Expirations are stored as UTC midnight; rendering that instant with local
  // getters shows the previous day in US timezones. Pin to the intended day.
  const d = toLocalDate(parsed);
  // Show year if different from current year
  const currentYear = new Date().getFullYear();
  if (d.getFullYear() !== currentYear) {
    return format(d, 'MMM d, yyyy');
  }
  return format(d, 'MMM d');
}

export function formatRelativeTime(date: Date | string | null | undefined): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? parseISO(date) : date;
  if (!isValid(d)) return '-';
  return formatDistanceToNow(d, { addSuffix: true });
}

export function getDaysToExpiration(date: Date | string | null | undefined): number {
  if (!date) return 0;
  const d = typeof date === 'string' ? parseISO(date) : date;
  if (!isValid(d)) return 0;
  // Calendar days, not elapsed 24h periods: differenceInDays truncates, which
  // made tomorrow's expiration read "0 DTE" for most of the day. toLocalDate
  // pins DB values (stored as UTC midnight) to their intended calendar day.
  return differenceInCalendarDays(toLocalDate(d), new Date());
}

export function formatDTE(date: Date | string | null | undefined): string {
  const days = getDaysToExpiration(date);
  if (days < 0) return 'Expired';
  if (days === 0) return 'Today';
  if (days === 1) return '1 day';
  return `${days} days`;
}

// Option symbol formatting (OCC format: AAPL240119C00150000)
export interface ParsedOptionSymbol {
  underlying: string;
  expiration: Date;
  type: 'call' | 'put';
  strike: number;
}

export function parseOccSymbol(symbol: string): ParsedOptionSymbol | null {
  // OCC format: ROOT (padded to 6 chars with spaces) + YYMMDD + C/P + 00000000 (8-digit strike * 1000)
  // Strip spaces before parsing since Schwab pads with spaces (e.g., "SCHW  260402P00092000")
  const cleaned = symbol.replace(/\s+/g, '');
  const match = cleaned.match(/^([A-Z]+)(\d{6})([CP])(\d{8})$/);
  if (!match) return null;

  const [, underlying, dateStr, typeChar, strikeStr] = match;
  const year = 2000 + parseInt(dateStr.slice(0, 2), 10);
  const month = parseInt(dateStr.slice(2, 4), 10) - 1;
  const day = parseInt(dateStr.slice(4, 6), 10);
  const strike = parseInt(strikeStr, 10) / 1000;

  return {
    underlying,
    expiration: new Date(year, month, day),
    type: typeChar === 'C' ? 'call' : 'put',
    strike,
  };
}

export function formatOptionSymbol(symbol: string): string {
  const parsed = parseOccSymbol(symbol);
  if (!parsed) return symbol;

  const { underlying, expiration, type, strike } = parsed;
  const dateStr = format(expiration, 'MMM d');
  const strikeStr = strike % 1 === 0 ? strike.toString() : strike.toFixed(2);
  const typeStr = type === 'call' ? 'C' : 'P';

  return `${underlying} ${dateStr} ${strikeStr}${typeStr}`;
}

export function formatOptionSymbolCompact(symbol: string): string {
  const parsed = parseOccSymbol(symbol);
  if (!parsed) return symbol;

  const { underlying, expiration, type, strike } = parsed;
  const dateStr = format(expiration, 'M/d');
  const strikeStr = strike % 1 === 0 ? strike.toString() : strike.toFixed(1);
  const typeStr = type === 'call' ? 'C' : 'P';

  return `${underlying} ${dateStr} $${strikeStr}${typeStr}`;
}

// Strategy display names
export const strategyDisplayNames: Record<StrategyType, string> = {
  single_call: 'Long Call',
  single_put: 'Long Put',
  covered_call: 'Covered Call',
  cash_secured_put: 'Cash-Secured Put',
  vertical_spread: 'Vertical Spread',
  iron_condor: 'Iron Condor',
  iron_butterfly: 'Iron Butterfly',
  straddle: 'Straddle',
  strangle: 'Strangle',
  calendar_spread: 'Calendar Spread',
  diagonal_spread: 'Diagonal Spread',
  custom: 'Custom',
};

export function formatStrategyType(type: StrategyType): string {
  return strategyDisplayNames[type] || type;
}

// Action display
export const actionDisplayNames: Record<OptionAction, string> = {
  BTO: 'Buy to Open',
  STO: 'Sell to Open',
  BTC: 'Buy to Close',
  STC: 'Sell to Close',
};

export function formatAction(action: OptionAction): string {
  return actionDisplayNames[action] || action;
}

export function getActionColor(action: OptionAction): 'profit' | 'loss' | 'neutral' {
  if (action === 'STO' || action === 'STC') return 'profit';
  return 'loss';
}

// Alert types
export const alertTypeDisplayNames: Record<AlertType, string> = {
  profit_target: 'Profit Target',
  loss_limit: 'Loss Limit',
  roll_opportunity: 'Roll Opportunity',
  strike_proximity: 'Strike Proximity',
  expiration_warning: 'Expiration Warning',
  early_assignment_risk: 'Early Assignment Risk',
  earnings_warning: 'Earnings Warning',
};

export function formatAlertType(type: AlertType): string {
  return alertTypeDisplayNames[type] || type;
}

export const alertSeverityColors: Record<AlertSeverity, string> = {
  info: 'blue',
  warning: 'yellow',
  critical: 'red',
};

// Status formatting
export function formatStatus(status: 'open' | 'closed' | 'partial'): string {
  switch (status) {
    case 'open':
      return 'Open';
    case 'closed':
      return 'Closed';
    case 'partial':
      return 'Partial';
    default:
      return status;
  }
}

export function getStatusColor(status: 'open' | 'closed' | 'partial'): string {
  switch (status) {
    case 'open':
      return 'green';
    case 'closed':
      return 'gray';
    case 'partial':
      return 'yellow';
    default:
      return 'gray';
  }
}
